import { useQueryGetAllBots } from "@apis:bot/bot";
import {
  Box,
  Chip,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { FormProps } from "@pages:settings/Tenent/Bots/BotSpecfications/types";
import { FountainBlue } from "@themes:palette";
import { camelToSnakeCase } from "@utils:object";
import Ajv from "ajv/dist/2020";
import addFormats from "ajv-formats";
import { defaultTheme, JsonData, JsonEditor } from "json-edit-react";
import { isEmpty, noop } from "lodash";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { FaClone } from "react-icons/fa6";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { MdRedo, MdUndo } from "react-icons/md";
import useUndo from "use-undo";

import jsonSchema from "./ai_settings_schema.json";

const ajv = new Ajv();
addFormats(ajv);

const validateJsonSchema = ajv.compile(jsonSchema);

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    border: "1px solid #dadde9",
  },
}));

export const AISettings = ({
  control,
  formState,
  setValue,
  watch,
  botId,
  trigger,
}: FormProps) => {
  const [
    { present },
    { set: setData, reset, undo: undoData, redo: redoData, canUndo, canRedo },
  ] = useUndo({});

  const undo = () => undoData();
  const redo = () => redoData();

  useEffect(() => {
    // sync useUndo with react-hook-form,
    // because we update the state of useUndo not directly from the form
    if (!isEmpty(present)) {
      setValue("ai_settings", present as any, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  }, [present, setValue]);

  useEffect(() => {
    // validate aiSettings when the component mounts
    const touchAiSettings = async () => {
      await trigger("ai_settings");
    };
    touchAiSettings().then(noop);
  }, [trigger]);

  const aiSettings = watch("ai_settings");
  const isSetSettingsForUpdate = useRef(false);

  useEffect(() => {
    // when we update the bot that has aiSettings,
    // we need to set the aiSettings to useUndo to begin keeping track of changes
    if (!isEmpty(aiSettings)) {
      if (isSetSettingsForUpdate.current) {
        return;
      }
      isSetSettingsForUpdate.current = true;

      reset(aiSettings);
    }
  }, [aiSettings, reset]);

  const { data: listBots, isLoading } = useQueryGetAllBots();
  const isCloneButtonDisabled = isLoading || isEmpty(listBots);

  const [searchText, setSearchText] = useState("");
  const errorMessage = formState?.errors?.ai_settings?.message;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleCloneSetting = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "10px",
          zIndex: 100,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          paddingX: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          {errorMessage ? (
            <HtmlTooltip
              title={
                <>
                  {errorMessage?.split("\n")?.map((message, i) => (
                    <Typography
                      key={i}
                      sx={{ display: "block", marginY: "8px" }}
                    >
                      {message}
                    </Typography>
                  ))}
                </>
              }
            >
              <Chip
                label="Invalid Settings"
                sx={{
                  background: "#874b4b" + 40,
                  color: "#874b4b",
                  height: "32px",
                }}
                icon={<IoMdCheckmarkCircle size={20} color="white" />}
              />
            </HtmlTooltip>
          ) : (
            <Chip
              label="Valid Settings"
              sx={{
                background: "#3cbd6a" + 40,
                color: "#3cbd6a",
                height: "32px",
              }}
              icon={<IoMdCheckmarkCircle size={20} color="white" />}
            />
          )}
          <Box>
            <Button startIcon={<MdUndo />} disabled={!canUndo} onClick={undo}>
              Undo
            </Button>
            <Button startIcon={<MdRedo />} disabled={!canRedo} onClick={redo}>
              Redo
            </Button>
          </Box>
          <Box>
            <Button
              onClick={handleCloneSetting}
              startIcon={<FaClone />}
              disabled={isCloneButtonDisabled}
            >
              Clone Settings
            </Button>
          </Box>
        </Box>
        <TextField
          sx={{
            backgroundColor: "#f6f6f6",
            borderRadius: 50,
            width: searchText ? "30%" : "10%",
            height: "30px",
            "&:focus-within": {
              width: "30%",
            },
            transition: "width 0.3s",
            borderColor: searchText ? FountainBlue[600] : undefined,
            "& .MuiOutlinedInput-root": {
              height: "32px",
              borderRadius: 50,
              "&:hover fieldset": {
                borderColor: FountainBlue[600],
              },
              fieldset: {
                borderWidth: 2,
                borderColor: searchText ? FountainBlue[600] : undefined,
              },
            },
          }}
          placeholder="🔍"
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Box>
      <Controller
        name="ai_settings"
        control={control}
        rules={{
          validate: (value) => {
            const isValid = validateJsonSchema(camelToSnakeCase(value));
            if (!isValid) {
              return validateJsonSchema.errors
                ?.map(
                  (error) =>
                    `${error.instancePath}${error.instancePath ? ": " : ""}${error.message}`,
                )
                .join("\n");
            }
            return true;
          },
        }}
        render={() => (
          <JsonEditor
            data={present}
            setData={setData as (data: JsonData) => void}
            rootName={"ai_settings"}
            theme={[defaultTheme, {}, { container: { paddingTop: "50px" } }]}
            indent={2}
            showErrorMessages
            collapse={2}
            collapseAnimationTime={300}
            showCollectionCount
            enableClipboard
            restrictDrag
            searchFilter="all"
            searchText={searchText}
            defaultValue={"New data!"}
            showArrayIndices
            showStringQuotes
            minWidth="100%"
            maxWidth="100%"
            stringTruncate={90}
          />
        )}
      />
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {listBots
          ?.filter((bot) => bot.id !== botId)
          ?.map((bot, i, arr) => [
            <MenuItem
              key={`menu-item-${bot.id}`}
              sx={{ alignItems: "center", minWidth: "200px" }}
              dense
              onClick={() => {
                setData(bot.ai_settings);
                handleClose();
              }}
            >
              Bot: {bot.name}
            </MenuItem>,
            i !== arr.length - 1 && <Divider key={`divider-${bot.id}`} />,
          ])
          .flat()
          .filter(Boolean) || []}
      </Menu>
    </Box>
  );
};
